import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/**
 * Steuert die Stapelreihenfolge paralleler In-App-Fenster
 * (Stundennachweis, Dokumenteneditor/Rechnungsentwurf, Projektübersicht).
 *
 * Zuletzt per `focusSurface` aktivierte Fläche liegt oben.
 */
export type InAppSurface = "timesheet" | "documentEditor" | "projectOverview";

interface InAppWindowFocusContextValue {
  frontSurface: InAppSurface | null;
  focusSurface: (surface: InAppSurface) => void;
}

const InAppWindowFocusContext = createContext<InAppWindowFocusContextValue | null>(null);

export function InAppWindowFocusProvider({ children }: { children: ReactNode }) {
  const [frontSurface, setFrontSurface] = useState<InAppSurface | null>(null);

  const focusSurface = useCallback((surface: InAppSurface) => {
    setFrontSurface(surface);
  }, []);

  const value = useMemo(
    () => ({ frontSurface, focusSurface }),
    [frontSurface, focusSurface],
  );

  return (
    <InAppWindowFocusContext.Provider value={value}>{children}</InAppWindowFocusContext.Provider>
  );
}

export function useInAppWindowFocus(): InAppWindowFocusContextValue {
  const ctx = useContext(InAppWindowFocusContext);
  if (!ctx) {
    return {
      frontSurface: null,
      focusSurface: () => undefined,
    };
  }
  return ctx;
}

/** Optionaler Context-Zugriff (z. B. außerhalb des Providers → null). */
export function useInAppWindowFocusOptional(): InAppWindowFocusContextValue | null {
  return useContext(InAppWindowFocusContext);
}
